import * as React from 'react';
import { useTransitionStore } from './trasnsition-store';
import { IAllTransitions } from '@agilepoint/utils-type';
import { animated, SpringValues, TransitionRenderFn, useTransition } from 'react-spring';
import { isArray } from '@agilepoint/utils-common';
import { CSSProperties } from 'styled-components';

export type TransitionProps<T extends IAllTransitions = IAllTransitions> = {
    transitions?: keyof T | (keyof T)[];
    config?: any[];
    items?: any | any[];
    trail?: number;
    keySelector?: (item: any) => string;
    render?: TransitionRenderFn;
};

const mergeCSSProps = (left?: CSSProperties, right?: CSSProperties) => {
    const final = { ...left ?? {}, ...right };
    if (right?.transform) {
        const currentTransform = left?.transform ?? '';
        if (!currentTransform.includes(right.transform.split('(')[0])) {
            final.transform = `${currentTransform} ${right.transform}`.trimStart();
        }
    }
    return final;
};

export const Transition = (props: TransitionProps) => {
    const { config = [], items, trail, transitions = [], render } = props;
    const store = useTransitionStore();

    const transitionHookOptions = React.useMemo(() => {
        const transitionsArray = (isArray(transitions) ? transitions : [transitions]);
        const transitionsConfig = (isArray(config) ? config : [config]);
        const appliedTransitions = transitionsArray?.map((i, index) => store.apply(i, transitionsConfig[index]));
        return appliedTransitions.reduce((prev: any, current) => ({
            ...prev,
            initial: Object.assign({}, prev.initial, current?.initial),
            from: current?.from ? mergeCSSProps(prev.from, current?.from as any) : prev?.from,
            enter: current?.enter ? mergeCSSProps(prev.enter, current?.enter as any) : prev.enter,
            leave: current?.leave ? mergeCSSProps(prev.leave, current?.leave as any) : prev.leave,
            update: current?.update ? mergeCSSProps(prev.update, current?.update as any) : prev.update
        }), { unique: true, from: {}, initial: {}, enter: [], leave: [], update: [] });
    }, [config, store, transitions]);

    const finalTransitionHookOptions = React.useMemo(() => ({
        ...transitionHookOptions, trail
    }), [transitionHookOptions, trail]);
    const transitionFunction = useTransition(items, finalTransitionHookOptions);

    const renderFunc:TransitionRenderFn = React.useMemo(() => render ?? ((styles: SpringValues<any>, item: any) =>
        item && React.createElement(animated.div, { style: styles }, item)), [render]);

    return <>{transitionFunction(renderFunc)}</>;
};
